import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';

import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AuthService } from '@app/core/services/auth.service';
import { CustomerOrderService } from '@app/core/services/customer-order-auth/customer-order.service';
import { GeneralService } from '@app/core/services/general.service';
import { PreSignUrlResponse, UploadAuthService } from '@app/core/services/upload/upload-auth.service';
import { AlertDialogComponent } from '@app/shared/components/alert-dialog/alert-dialog.component';
import { AutocompleteBackendInputComponent } from '@app/shared/components/autocomplete-backend-input/autocomplete-backend-input.component';
import { LoadingSpinnerButtonComponent } from '@app/shared/components/loading-spinner-button/loading-spinner-button.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { SelectionInputComponent } from '@app/shared/components/selection-input/selection-input.component';
import { TutorialComponent } from '@app/shared/components/tutorial/tutorial.component';
import { CRORE_ONE, LAKH_ONE } from '@app/shared/constants';
import {
  dateFormat,
  failSafeResponseStructure,
  numberFormat,
  undefOrEmpty,
  zohoCustomerStatementDownloadFromResponse,
} from '@app/shared/functions/utils';
import { SelectionOption, SelectionOptions } from '@app/shared/interfaces/selection-option';
import moment from 'moment';
import { Subscription, forkJoin } from 'rxjs';
import { ZohoStatementDownloadDialogComponent } from '../customer-orders-desktop/zoho-statement-download-dialog/zoho-statement-download-dialog.component';
import { AgingReportComponent, AgingReportData } from './aging-report/aging-report.component';

export interface CustomerAgingReportData {
  outstanding: string;
  notDue: string;
  due1To30Days: string;
  due31To60Days: string;
  due61To90Days: string;
  due90PlusDays: string;
}

interface HighlightStatement {
  totalTransactionAmount: string;
  numberOfMonths: string;
  numberOfProducts: string;
  numberOfDeliverySites: string;
}

interface CustomerInfo {
  name: string;
  logoImageUrl: string | null;
}

export const exchangeFieldNames: { [key: string]: string } = {
  project: 'projectId',
  categoryProduct: 'materialCategoryProduct',
  poNumber: 'quotationId',
};

@Component({
  selector: 'app-customer-orders-mobile',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    LoadingSpinnerButtonComponent,
    MatTabsModule,
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    MatButtonToggleModule,
    MatPaginator,
    MatPaginatorModule,
    MatChipsModule,
    MatTableModule,
    MatButtonModule,
    SelectionInputComponent,
    AutocompleteBackendInputComponent,
  ],
  templateUrl: './customer-orders-mobile.component.html',
  styleUrl: './customer-orders-mobile.component.scss',
})
export class CustomerOrdersMobileComponent implements OnInit, OnDestroy {
  tabIndex: number = 0;

  customerInfo!: CustomerInfo;

  highlightStatementTiles!: HighlightStatement;

  agingReport!: AgingReportData;
  isScreenDesktop!: boolean;

  searchForm: FormGroup;

  isLoading: boolean;
  orderDataLoader: boolean = false;

  outstandingInvoiceDataLoader: boolean = false;

  outstandingInvoicePageNumber: number = 1;
  outstandingInvoicePageSize: number = 20;
  totalOutstandingInvoicesDataLength: number = 0;

  filteredOptions: { [key: string]: SelectionOptions };

  queryGet!: Subscription;

  buttonToggleOptions: string = 'orders';
  orderTypeValue: string = 'completed';

  downloadZohoCustomerStatementButtonLoading: boolean = false;

  invoiceData: any = [];
  orderData: any = [];

  appliedFilters: { [key: string]: string } = {
    dateRange: 'PAST_SIX_MONTHS',
  };

  orderTypeOptions: SelectionOptions = [
    { value: 'Completed', key: 'completed' },
    { value: 'Ongoing', key: 'ongoing' },
    { value: 'Quoted', key: 'quoted' },
    { value: 'Open enquiry', key: 'openEnquiry' },
  ];

  @ViewChild('invoicePaginator') invoicePaginator!: MatPaginator;

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private generalService: GeneralService,
    private customerOrderService: CustomerOrderService,
    private uploadAuthService: UploadAuthService,
    private authService: AuthService
  ) {
    this.isLoading = true;
    this.searchForm = this.fb.group({
      dateRange: 'PAST_SIX_MONTHS',
      poNumber: null,
      project: null,
      categoryProduct: null,
      orderType: 'completed',
    });

    this.filteredOptions = {
      dateRange: [],
      poNumber: [],
      project: [],
      categoryProduct: [],
    };
  }

  ngOnInit() {
    this.isScreenDesktop = this.activatedRoute.snapshot.data['screenSize'] == 'desktop';

    // if (!this.isScreenDesktop) {
    //   this.mobileScreenDialog();
    // }

    this.generalService.getEnums(['DateRangeEnum']).subscribe((response) => {
      failSafeResponseStructure(response['dateRangeEnum'], false, true);

      this.filteredOptions['dateRange'] = response['dateRangeEnum'];
    });

    // if (this.authService.isFirstTimeUserLoginLocalStorageKey()) {
    //   this.onTutorial();

    //   this.authService.storeFirstTimeUserLoginLocalStorageKey();
    // }

    this.queryGet = forkJoin({
      requestCustomerInfo: this.customerOrderService.getApiCustomerInfo(),
      requestCustomerInfoHighlightStatement: this.customerOrderService.getApiCustomerInfoHighlightStatement(),
      requestCustomerAgingReport: this.customerOrderService.getApiCustomerAgingReport(),
      requestCustomerOutstandingInvoices: this.customerOrderService.getApiCustomerOutstandingInvoices(
        this.outstandingInvoicePageNumber,
        this.outstandingInvoicePageSize
      ),
      requestCompletedOrders: this.customerOrderService.getApiCompletedOrders(this.appliedFilters),
    }).subscribe({
      next: (responses) => {
        this.setCustomerInfo(responses.requestCustomerInfo);

        this.setHighlightStatement(responses.requestCustomerInfoHighlightStatement);

        this.setAgingReport(responses.requestCustomerAgingReport);

        this.setOutstandingInvoices(responses.requestCustomerOutstandingInvoices);

        this.setOrderData(responses.requestCompletedOrders);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  ngOnDestroy() {
    this.queryGet.unsubscribe();
  }

  onValChange(val: string) {
    this.buttonToggleOptions = val;
  }

  setOrderData(response: any) {
    this.orderData = [];

    if (!Object.prototype.hasOwnProperty.call(response, 'data')) {
      console.error('response does not have an attribute "data"');

      return;
    }

    if (!Array.isArray(response.data)) {
      console.error('response.data is not an array');
      return;
    }

    response.data.forEach((element: any) => {
      let itemDisplay: string = '-';
      const itemListLength: number = element?.itemList?.length;
      const totalQty = element?.itemList?.reduce(
        (sum: number, item: { qty: string | number }) => sum + Number(item.qty),
        0
      );
      if (element?.itemList?.length > 0) {
        itemDisplay =
          element?.itemList[0].materialCategory.label +
          (element.itemList.length > 1 ? ' ' + '+' + (itemListLength - 1) : '') +
          ' ( ' +
          numberFormat(totalQty) +
          ' ' +
          undefOrEmpty(element?.itemList[0].qtyUom?.value) +
          ' )';
      }

      let itemList = '';
      element?.itemList?.forEach((element1: any) => {
        itemList +=
          element1.materialCategory.label + ' (' + element1.qty + ' ' + undefOrEmpty(element1.qtyUom?.value) + ')\n';
      });
      itemList = itemList.slice(0, -1);

      this.orderData.push({
        id: element?.customerOrder?.id || element?.id,
        enquiryNum: element?.customerOrder?.enquiryNum || element?.enquiryNum,
        date: dateFormat(element?.customerOrder?.enquiryDate) || dateFormat(element?.enquiryDate),
        poNumber: element?.customerPoNum || '--',
        items: itemDisplay,
        quantity: totalQty,
        itemList: itemList,
        project: element?.customerOrder?.project?.name || element?.project?.name,
        projectAddress: element?.customerOrder?.project?.projectAddressFull || element?.project?.projectAddressFull,
        quotationAmt: element?.totalQuotationAmountWithGst || 0,
      });
    });

    this.orderDataLoader = false;
  }

  setOrderType(value: any) {
    if (!value) {
      this.searchForm.get('orderType')?.setValue(this.orderTypeValue, {
        emitEvent: false,
      });
      return;
    }
    this.orderTypeValue = value;
    this.applyOrderFilter();
  }

  applyOrderFilter(): void {
    let appliedFilters: { [key: string]: string } = {};

    for (const key in this.searchForm.value) {
      if (key === 'orderType') {
        continue;
      }

      let value: string | SelectionOption = this.searchForm.value[key];

      if (value === null) {
        continue;
      }

      if (value instanceof Object) {
        value = value['key'];
      }

      if (Object.keys(exchangeFieldNames).includes(key)) {
        appliedFilters[exchangeFieldNames[key]] = value;
      } else {
        appliedFilters[key] = value;
      }
    }

    this.orderDataLoader = true;

    switch (this.orderTypeValue) {
      case 'ongoing':
        this.customerOrderService.getApiOngoingOrders(appliedFilters).subscribe({
          next: (response) => {
            this.setOrderData(response);
          },
          error: (error) => {
            console.error('Error loading orders:', error);
            this.orderDataLoader = false;
          },
        });
        break;

      case 'quoted':
        this.customerOrderService.getApiQuotedOrders(appliedFilters).subscribe({
          next: (response) => {
            this.setOrderData(response);
          },
          error: (error) => {
            console.error('Error loading orders:', error);
            this.orderDataLoader = false;
          },
        });
        break;

      case 'openEnquiry':
        this.customerOrderService.getApiOpenOrders(appliedFilters).subscribe({
          next: (response) => {
            this.setOrderData(response);
          },
          error: (error) => {
            console.error('Error loading orders:', error);
            this.orderDataLoader = false;
          },
        });
        break;

      default:
        this.customerOrderService.getApiCompletedOrders(appliedFilters).subscribe({
          next: (response) => {
            this.setOrderData(response);
          },
          error: (error) => {
            console.error('Error loading orders:', error);
            this.orderDataLoader = false;
          },
        });
    }
  }

  setCustomerInfo(response: any) {
    this.customerInfo = {
      name: response.data.name,
      logoImageUrl: null,
    };

    if (response.data.logoImageUrl !== null) {
      this.uploadAuthService.getFileUrl(response.data.logoImageUrl).subscribe((response: PreSignUrlResponse) => {
        this.customerInfo.logoImageUrl = response.url;
      });
    }
  }

  setHighlightStatement(response: any) {
    let amountString: string;
    {
      const amount: number = Math.round(response.data?.totalNetInvoiceAmount);

      if (amount >= CRORE_ONE) {
        amountString = Math.round(amount / CRORE_ONE) + ' crore';

        if (Math.round(amount / CRORE_ONE) > 1) {
          amountString = amountString + 's';
        }
      } else if (amount >= LAKH_ONE) {
        amountString = Math.round(amount / LAKH_ONE) + ' lakh';

        if (Math.round(amount / LAKH_ONE) > 1) {
          amountString = amountString + 's';
        }
      } else {
        amountString = amount.toString();
      }

      amountString = '₹ ' + amountString;
    }

    this.highlightStatementTiles = {
      totalTransactionAmount: amountString,
      numberOfMonths: response.data?.monthsSinceFirstInvoice,
      numberOfProducts: response.data?.countHsnCodes,
      numberOfDeliverySites: response.data?.countDeliverySites,
    };
  }

  setAgingReport(response: any) {
    const data = response?.data || {};

    const defaultReport: AgingReportData = {
      outstanding: '0',
      notDue: '0',
      due: '0',
      due1To30Days: '0',
      due31To60Days: '0',
      due61To90Days: '0',
      due90PlusDays: '0',
    };

    this.agingReport = { ...defaultReport, ...data };

    (Object.keys(defaultReport) as (keyof AgingReportData)[]).forEach((key) => {
      if (data[key] === undefined) {
        console.warn(`⚠️ AgingReport missing field: ${key}`);
      }
    });
  }

  setOutstandingInvoices(response: any) {
    response.data?.forEach((element: any) => {
      this.invoiceData.push({
        invoiceNo: element.invoiceNumber,
        invoiceDate: dateFormat(element.invoiceDate),
        dueDate: dateFormat(element.dueDate),
        amount: numberFormat(element.netInvoiceAmount),
        outstanding: numberFormat(element.outstanding),
        daysPastDue: element.daysPastDue,
      });
    });

    this.totalOutstandingInvoicesDataLength = response.totalCount;
  }

  async onChangePage(event: any) {
    if (event.pageIndex == this.outstandingInvoicePageNumber) {
      this.outstandingInvoicePageNumber = this.outstandingInvoicePageNumber + 1;
      this.outstandingInvoiceDataLoader = true;

      this.customerOrderService
        .getApiCustomerOutstandingInvoices(this.outstandingInvoicePageNumber, this.outstandingInvoicePageSize)
        .subscribe((response) => {
          response.data?.forEach((element: any) => {
            this.invoiceData.push({
              invoiceNo: element.invoiceNumber,
              invoiceDate: dateFormat(element.invoiceDate),
              dueDate: dateFormat(element.dueDate),
              amount: numberFormat(element.netInvoiceAmount),
              outstanding: numberFormat(element.outstanding),
              daysPastDue: element.daysPastDue,
            });
          });

          this.totalOutstandingInvoicesDataLength = response.totalCount;
          this.outstandingInvoiceDataLoader = false;
        });
    }
  }

  onCategoryProductStringChange(searchString: string) {
    this.customerOrderService.searchAutocomplete('MATERIAL_CATEGORY_PRODUCT', searchString).subscribe({
      next: (response) => {
        if (!failSafeResponseStructure(response, true, true)) {
          return;
        }

        this.filteredOptions['categoryProduct'] = response.data;
      },
    });
  }

  onProjectStringChange(searchString: string) {
    this.customerOrderService.searchAutocomplete('PROJECT', searchString).subscribe({
      next: (response) => {
        if (!failSafeResponseStructure(response, true, true)) {
          return;
        }

        this.filteredOptions['project'] = response.data.map((project: any) => {
          return {
            key: project.id,
            value: project.name,
          };
        });
      },
    });
  }

  onPoNumberStringChange(searchString: string) {
    this.customerOrderService.searchAutocomplete('PO_NUMBER', searchString).subscribe({
      next: (response) => {
        if (!failSafeResponseStructure(response, true, true)) {
          return;
        }

        this.filteredOptions['poNumber'] = response.data;
      },
    });
  }

  onAgingReportClick() {
    const dialogRef = this.dialog.open(AgingReportComponent, {
      maxWidth: '80vw',
      data: this.agingReport,
      autoFocus: false,
    });
  }

  onTutorial() {
    this.dialog.open(TutorialComponent, {
      maxWidth: '1000px',
      maxHeight: '600px',
    });
  }

  mobileScreenDialog() {
    this.dialog.open(AlertDialogComponent, {
      data: {
        message: 'For best experience, please visit \ncustomers.zippmat.in \non your desktop.\n',
      },
    });
  }

  downloadZohoCustomerStatement() {
    const dialogRef = this.dialog.open(ZohoStatementDownloadDialogComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: 'auto',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.downloadZohoCustomerStatementButtonLoading = true;

        const zohoStatementStartDate = result.startDate ? moment(result.startDate).format('YYYY-MM-DD') : '';
        const zohoStatementEndDate = result.endDate ? moment(result.endDate).format('YYYY-MM-DD') : '';

        this.customerOrderService
          .getZohoCustomerStatementDownload(zohoStatementStartDate, zohoStatementEndDate)
          .subscribe({
            next: (response) => {
              zohoCustomerStatementDownloadFromResponse(response, this.customerInfo?.name);
              this.downloadZohoCustomerStatementButtonLoading = false;
            },
            error: (error) => {
              console.error('Error while downloading Customer Statement', error);
              this.downloadZohoCustomerStatementButtonLoading = false;
            },
          });
      }
    });
  }

  navigateToOrderPage(orderId: string) {
    window.open(`/customer/customer-order/${orderId}`, '_blank');
  }

  onLogout() {
    this.authService.logout();
  }
}
